/**
 * useCreateCustomer — the manual create-customer mutation (POST /customers).
 *
 * Calls the typed client (apiClient.customers.create) and, on success, invalidates
 * every Kunden-grid query so the new customer appears in the list. The caller owns
 * navigation to the new customer's detail + the success/error toast.
 *
 * [Source: apps/web/src/features/invoices/hooks/useInvoiceDecision.ts] (mutation + invalidate pattern)
 * [Source: architecture/frontend-architecture.md#3-server-state-tanstack-query-v5]
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type { CreateCustomerBody } from '../../../lib/api-client';
import type { CreateCustomerResponseDto } from '@brokly/shared';

export function useCreateCustomer() {
  const qc = useQueryClient();

  return useMutation<CreateCustomerResponseDto, Error, CreateCustomerBody>({
    mutationFn: (body: CreateCustomerBody) => apiClient.customers.create(body),
    onSuccess: () => {
      // Refresh every Kunden-grid page (all filter/mode variants share the ['customers'] prefix).
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}
